import { IMAGE_ACCEPT } from '@/utils/get-mime';
import { asyncLoadImg } from '@q/solib-utils';

export const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type,
  quality = 0.92
) => {
  return new Promise<Blob>(resolve =>
    canvas.toBlob(blob => resolve(blob), type, quality)
  );
};
function getExt(type) {
  return type === 'image/png' ? '.png' : '.jpg';
}
// 判断画布中是否存在 alpha 通道（透明像素）
export const checkAlphaChannel = (ctx, width, height) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] < 255) return true;
  }
  return false;
};

const rules = {
  /**
   * ### 图像扩展支持图片格式：
   *  1. 仅支持JPG、JPEG、PNG格式，建议使用JPG格式。
   *  2. 图片文件大小：最大 5 MB。
   *  3. 图片分辨率：最小 64x64，最大 4096x4096
   */
  expand: {
    format: [IMAGE_ACCEPT.jpg, IMAGE_ACCEPT.jpeg, IMAGE_ACCEPT.png],
    maxSize: 5 * 1024 * 1024,
    minRes: 64,
    maxWidth: 4096,
    maxHeight: 4096,
  },
  /**
   * ### 局部重绘输入图要求：
   * 1. 图片格式：仅支持JPG、JPEG、PNG格式，建议使用JPG格式。
   * 2. 图片文件大小：最大 5 MB。
   * 3. 图片分辨率：最小 64x64，最大 4096x4096
   */
  repaint: {
    format: [IMAGE_ACCEPT.jpg, IMAGE_ACCEPT.jpeg, IMAGE_ACCEPT.png],
    maxSize: 5 * 1024 * 1024,
    minRes: 64,
    maxWidth: 4096,
    maxHeight: 4096,
  },
  /**
   * ### 图像擦除输入图要求：
   *  1. 图片格式：仅支持JPG、JPEG、PNG格式，建议使用JPG格式。
   *  2. 图片文件大小：最大 5 MB。
   *  3. 图片分辨率：最小 64x64，最大 4096x4096 。
   */
  erase: {
    format: [IMAGE_ACCEPT.jpg, IMAGE_ACCEPT.jpeg, IMAGE_ACCEPT.png],
    maxSize: 5 * 1024 * 1024,
    minRes: 64,
    maxWidth: 4096,
    maxHeight: 4096,
  },
  /**
   * ### 抠图（主题分割）输入图要求：
   *  1. 图片格式：JPG、JPEG、PNG、BMP等常见格式，建议使用JPG/PNG格式。
   *  2. 最小 128 x 128 像素，最大 4096 x 4096 像素；
   *  3. base64格式下单张图片最大4.7Mb。
   */
  matting: {
    format: [
      IMAGE_ACCEPT.jpg,
      IMAGE_ACCEPT.jpeg,
      IMAGE_ACCEPT.png,
      IMAGE_ACCEPT.webp,
      IMAGE_ACCEPT.bmp,
    ],
    maxSize: 5 * 1024 * 1024,
    minRes: 128,
    maxWidth: 4096,
    maxHeight: 4096,
  },
  /**
   * ### 变清晰输入图要求：
   *  1. 图片格式：JPG、JPEG、PNG、BMP等常见格式，建议使用JPG格式。
   *  2. 图片大小及分辨率：最大 5 MB;
   *  3. 输入图片尺寸 width 满足 [50, 2128], height 满足 [50, 4046]。
   */
  sharpen: {
    format: [
      IMAGE_ACCEPT.jpg,
      IMAGE_ACCEPT.jpeg,
      IMAGE_ACCEPT.png,
      IMAGE_ACCEPT.webp,
      IMAGE_ACCEPT.bmp,
    ],
    maxSize: 5 * 1024 * 1024,
    minRes: 50,
    maxHeight: 4046,
    maxWidth: 2128,
  },
};

export const getAdjustedSize = (width, height, maxW, maxH, minRes) => {
  let scale = 1;

  // 处理超过最大尺寸的情况
  if (width > maxW || height > maxH) {
    scale = Math.min(maxW / width, maxH / height);
  }

  // 处理小于最小尺寸的情况
  if (width < minRes || height < minRes) {
    // 找出较小的边，并计算将其放大到minRes所需的比例
    const minScale = Math.max(minRes / width, minRes / height);
    scale = minScale; // 等比放大
  }

  width = width * scale;
  height = height * scale;

  return {
    width: Math.round(width),
    height: Math.round(height),
  };
};
export async function verificationImage(file, type) {
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('size');
  }

  const rule = rules[type];
  if (!rule) throw new Error(`unknownType`);

  const src = URL.createObjectURL(file);

  const img = await asyncLoadImg(src).catch(e => {
    console.error(e);
    throw new Error('format');
  });

  let width = img.naturalWidth;
  let height = img.naturalHeight;
  const ratio = width / height;

  if (ratio < 1 / 3 || ratio > 3) {
    throw new Error('ratio');
  }

  const { minRes, maxSize, format: supportedFormats, maxWidth, maxHeight } = rule;

  const maxRes = 1920;

  // 判断是否合规（尺寸、分辨率、格式、大小）
  const sizeOk = file.size <= maxSize;
  const formatOk = supportedFormats.includes(file.type);
  const resolutionOk = width >= minRes && height >= minRes;
  const dimensionOk = width <= maxWidth && height <= maxHeight;

  // 如果所有条件都满足，直接返回true
  return sizeOk && formatOk && resolutionOk && dimensionOk;
}

export async function processImageToFitRules(file, type) {
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('size');
  }

  const rule = rules[type];
  if (!rule) throw new Error(`unknownType`);

  const src = URL.createObjectURL(file);

  const img = await asyncLoadImg(src).catch(e => {
    console.error(e);
    throw new Error('format');
  });

  let width = img.naturalWidth;
  let height = img.naturalHeight;
  const ratio = width / height;

  if (ratio < 1 / 3 || ratio > 3) {
    throw new Error('ratio');
  }

  const { minRes, maxSize, format: supportedFormats, maxHeight, maxWidth } = rule;

  const maxRes = 1920;

  // 判断是否合规（尺寸、分辨率、格式、大小）
  const sizeOk = file.size <= maxSize;
  const formatOk = supportedFormats.includes(file.type);
  const resolutionOk = width >= minRes && height >= minRes;
  const dimensionOk = width <= maxWidth && height <= maxHeight;
  // 如果所有条件都满足，直接返回原图
  if (sizeOk && formatOk && resolutionOk && dimensionOk) {
    return file;
  }

  // 调整图片处理逻辑
  if (file.size > rule.maxSize) {
    // 如果图片大于maxSize，则直接压缩到1920
    ({ width, height } = getAdjustedSize(
      width,
      height,
      maxRes,
      maxRes,
      minRes
    ));
  } else if (!dimensionOk) {
    // 如果图片不大于maxSize但宽或高超过4096，则压缩到4096
    ({ width, height } = getAdjustedSize(
      width,
      height,
      rule.maxWidth,
      rule.maxHeight,
      minRes
    ));
  } else if (!resolutionOk) {
    // 如果图片宽或高小于minRes，则将图片小的边放大到minRes，另一边等比放大
    ({ width, height } = getAdjustedSize(
      width,
      height,
      rule.maxWidth,
      rule.maxHeight,
      minRes
    ));
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // 决定输出格式（如需转码）
  let outputType = file.type;
  if (!formatOk) {
    const hasAlpha = checkAlphaChannel(ctx, width, height);
    outputType = hasAlpha ? 'image/png' : 'image/jpeg';
  }
  // 压缩逻辑
  let quality = 0.92;
  let blob = await canvasToBlob(canvas, outputType, quality);
  if (blob.size > maxSize && quality > 0.1) {
    quality = 0.1;
    blob = await canvasToBlob(canvas, outputType, quality);
  }
  // 如果压缩后仍然超过大小限制，则缩小图片尺寸
  if (blob.size > maxSize) {
    ({ width, height } = getAdjustedSize(width, height, 1920, 1920, minRes));
    // 重新创建canvas并绘制
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    // 使用较低质量再次压缩
    blob = await canvasToBlob(canvas, outputType, 0.1);
    // 如果仍然超过大小限制，则抛出错误
    if (blob.size > maxSize) {
      throw new Error('size');
    }
  }

  const finalFile = new File(
    [blob],
    file.name.replace(/\.[^.]+$/, '') + getExt(outputType),
    { type: outputType }
  );

  return finalFile;
}

export const urlToFile = async (
  url: string,
  filename: string = 'image.png',
  mimeType: string = 'image/png'
): Promise<File> => {
  try {
    // 获取图片数据
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      );
    }

    // 将响应转换为Blob
    const blob = await response.blob();

    // 创建File对象
    const file = new File([blob], filename, { type: mimeType || blob.type });
    return file;
  } catch (error) {
    console.error('Error converting URL to File:', error);
    throw error;
  }
};
